<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, reactive, ref, watch } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { Mutation, PermissionOrganization, Query, UpdateStep } from '@/generated/graphql'
import gql from 'graphql-tag'
import { useRoute } from 'vue-router'
import useVuelidate from '@vuelidate/core'
import Breadcrumb from '@/components/Breadcrumb.vue'
import WhereUsedPanel from '@/components/step/WhereUsedPanel.vue'
import StepPreviewDialog from '@/components/step/StepPreviewDialog.vue'
import TextField from '@/components/input/TextField.vue'
import TextAreaField from '@/components/input/TextAreaField.vue'
import StepTypePicker from '@/components/input/StepTypePicker.vue'
import StepWarningField from '@/components/step/StepWarningField.vue'
import StepIconsField from '@/components/step/StepIconsField.vue'
import DurationField from '@/components/input/DurationField.vue'
import StepUploadList from '@/components/stepUpload/StepUploadList.vue'
import ArticleAllocationList from '@/components/article/ArticleAllocationList.vue'
import StepCheckConfigurations from '@/components/checkConfiguration/StepCheckConfigurations.vue'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'
import { scrollToFirstInvalidField } from '@/validation'
import { createStepValidationRules } from '@/validation/step'
import useAuthorization from '@/composables/useAuthorization'

const { t } = useI18n()
const route = useRoute()
const { hasOrgAuthz } = useAuthorization()
const stepId = computed(() => route.params.stepId as string)
const canWrite = computed(() => hasOrgAuthz(PermissionOrganization.OrgProductWrite))

const fetchQuery = useQuery<Query, { id: string }>(
  gql`
    query LibraryStepViewData($id: ID!) {
      product {
        step(id: $id) {
          id
          name
          instructionText
          warningText
          durationInSeconds
          toolIds
          stepType {
            id
          }
        }
      }
    }
  `,
  () => ({ id: stepId.value }),
)

type Model = Partial<UpdateStep>
const model = reactive<Model>({})
const validation = useVuelidate<Model>(createStepValidationRules, model, { $scope: false })
const notifySaved = ref(false)
const whereUsedOpen = ref(false)
const previewOpen = ref(false)

watch(
  () => fetchQuery.result.value?.product.step,
  (step) => {
    if (!step) return
    model.id = step.id
    model.name = step.name
    model.instructionText = step.instructionText
    model.warningText = step.warningText
    model.durationInSeconds = step.durationInSeconds
    model.stepTypeId = step.stepType.id
    model.toolIds = step.toolIds
  },
  { immediate: true },
)

const updateMutation = useMutation<Mutation, { command: UpdateStep }>(gql`
  mutation LibraryStepViewUpdate($command: UpdateStep!) {
    product {
      updateStep(command: $command) {
        id
      }
    }
  }
`)
function save() {
  validation.value.$touch()
  if (validation.value.$invalid) {
    scrollToFirstInvalidField()
    return
  }
  updateMutation.mutate({ command: model as UpdateStep }).then(() => {
    notifySaved.value = true
  })
}
</script>

<template>
  <breadcrumb />
  <h1 class="d-flex align-center">
    {{ model.name || t('view.organization.library.libraryStep') }}
    <v-spacer />
    <v-btn
      v-if="fetchQuery.result.value"
      variant="text"
      prepend-icon="visibility"
      @click="previewOpen = true"
    >
      {{ t('component.stepPreviewDialog.button') }}
    </v-btn>
    <v-btn
      v-if="fetchQuery.result.value"
      variant="text"
      prepend-icon="account_tree"
      @click="whereUsedOpen = true"
    >
      {{ t('view.organization.library.whereUsedTitle') }}
    </v-btn>
  </h1>

  <step-preview-dialog
    v-model="previewOpen"
    :step-id="stepId"
    :draft-step-id="stepId"
    :draft="model"
  />

  <v-card v-if="fetchQuery.result.value">
    <v-card-text>
      <text-field
        v-model="model.name"
        :label="t('entity.step.field.name')"
        required
        :validation="validation.name"
        :disabled="!canWrite"
      />
      <step-type-picker
        v-model="model.stepTypeId"
        :label="t('entity.step.field.stepType')"
        required
        :validation="validation.stepTypeId"
        :disabled="!canWrite"
      />
      <duration-field
        v-model="model.durationInSeconds"
        :label="t('entity.step.field.durationInSeconds')"
        required
        :validation="validation.durationInSeconds"
        :disabled="!canWrite"
      />
      <text-area-field
        v-model="model.instructionText"
        :label="t('entity.step.field.instructionText')"
        :validation="validation.instructionText"
        markdown
        :disabled="!canWrite"
      />
      <step-warning-field v-model="model.warningText" :disabled="!canWrite" />
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        v-if="canWrite"
        color="primary"
        variant="elevated"
        :loading="updateMutation.loading.value"
        @click="save()"
      >
        {{ t('button.save') }}
      </v-btn>
    </v-card-actions>
  </v-card>

  <where-used-panel v-model="whereUsedOpen" :step-id="stepId" />

  <template v-if="fetchQuery.result.value">
    <h3>{{ t('entity.article.plural') }}</h3>
    <article-allocation-list label="Parts" :step-id="stepId" />

    <h3>{{ t('entity.step.field.icons') }}</h3>
    <v-card>
      <v-card-text>
        <step-icons-field :step-id="stepId" :disabled="!canWrite" />
      </v-card-text>
    </v-card>

    <h3>{{ t('entity.stepUpload.plural') }}</h3>
    <step-upload-list :step-id="stepId" />

    <h3>{{ t('entity.checkConfiguration.plural') }}</h3>
    <step-check-configurations :step-id="stepId" />
  </template>

  <div class="pb-8" />

  <notification-saved v-model="notifySaved" />
</template>

<style scoped lang="scss">
h3 {
  margin-top: 2em;
  margin-bottom: 1em;
}
</style>
